import React, { useMemo, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { Lock, Mail } from 'lucide-react';
import { ContentHeading1, ContentHeading2, ContentText, ContentWrapper } from '@/components/ui/ContentText';
import { api } from '@/services/api';

const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const emailFromUrl = params.get('email') ?? '';
  const tokenFromUrl = params.get('token') ?? '';

  const [email, setEmail] = useState(emailFromUrl);
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return email.trim() !== '' && token.trim() !== '' && password.length >= 8 && password === passwordConfirm;
  }, [email, token, password, passwordConfirm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!canSubmit) return;

    try {
      setSubmitting(true);
      await api.postResetPassword({ token, email, password, password_confirmation: passwordConfirm });
      setMessage('Senha redefinida com sucesso! Você já pode acessar com sua nova senha.');
      // Optionally redirect to login after a short delay
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Não foi possível redefinir a senha. Verifique os dados e tente novamente.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContentWrapper className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <ContentHeading1 className="text-3xl font-bold text-gray-900">Redefinir senha</ContentHeading1>
          <ContentText className="mt-2 text-sm text-gray-600">Escolha uma nova senha para sua conta.</ContentText>
        </div>

        <Card className="bg-white shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <ContentHeading2 className="text-xl font-semibold text-gray-900 mb-6">Nova senha</ContentHeading2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="assertive">
                  <ContentText className="text-sm text-red-600">{error}</ContentText>
                </div>
              )}

              {message && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md" role="status" aria-live="polite">
                  <ContentText className="text-sm text-green-700">{message}</ContentText>
                </div>
              )}

              <Input
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                leftIcon={<Mail className="h-4 w-4" />}
                required
                disabled={submitting}
              />

              <Input
                label="Token"
                type="text"
                name="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Cole o token recebido por email"
                required
                disabled={submitting}
              />

              <Input
                label="Nova senha"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo de 8 caracteres"
                leftIcon={<Lock className="h-4 w-4" />}
                required
                disabled={submitting}
              />

              <Input
                label="Confirmar senha"
                type="password"
                name="password_confirmation"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Repita a nova senha"
                leftIcon={<Lock className="h-4 w-4" />}
                required
                disabled={submitting}
              />
            </div>

            <Button type="submit" className="w-full" loading={submitting} disabled={submitting || !canSubmit}>
              {submitting ? 'Enviando...' : 'Redefinir senha'}
            </Button>
          </form>
        </Card>

        <div className="text-center">
          <Link to="/login" className="text-sm text-blue-600 hover:underline">Voltar ao login</Link>
        </div>
      </div>
    </ContentWrapper>
  );
};

export default ResetPasswordPage;
